export function getContrastColor(hexColor: string): string {
  // Remove the hash if present
  const color = hexColor.replace('#', '');

  // Convert hex to RGB
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);

  // Calculate luminance using the relative luminance formula
  // See: https://www.w3.org/TR/WCAG20/#relativeluminancedef
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return white for dark colors, black for light colors
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

/**
 * Darken a hex color by the given amount
 *
 * @param hexColor - The color to darken
 * @param amount - How much to darken it by, 0 (unchanged) to 1 (black)
 * @returns The darkened color, or the original if it isn't a valid hex color
 */
export function darkenHexColor(hexColor: string, amount: number = 0.25): string {
  if (!isValidHexColor(hexColor)) {
    return hexColor;
  }

  const color = hexColor.trim().replace('#', '');

  // expand shorthand (#abc) so every channel is two characters
  const expanded =
    color.length === 3
      ? color
          .split('')
          .map((channel) => channel + channel)
          .join('')
      : color;

  const scale = Math.min(Math.max(1 - amount, 0), 1);

  const darkened = [0, 2, 4]
    .map((index) => Math.round(parseInt(expanded.substring(index, index + 2), 16) * scale))
    .map((channel) => channel.toString(16).padStart(2, '0'))
    .join('');

  return `#${darkened}`;
}

export function isValidHexColor(color: string): boolean {
  const trimmed = color.trim().replace(/^#/, '');
  return /^[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(trimmed);
}
